import React from 'react'
import {defineMessages, FormattedDate} from 'react-intl'
import {useDispatch} from 'react-redux'
import {SoldOutBase} from 'components/Unavailability/SoldOut/SoldOutBase'
import {useHotelRoomsFilters} from 'hooks/useHotelRoomsFilters'
import {showDatePicker} from 'modules/overlay/actions'

import {Button} from '@daedalus/atlas/Button'
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper'
import {Text} from '@daedalus/atlas/Text'
import {useBrand} from '@daedalus/core/src/_web/brand/hooks/useBrand'
import {DatePickerType} from '@daedalus/core/src/datePicker/types'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

interface Props {
  checkIn: string
  checkOut: string
  hasOtherOffers?: boolean
  hasActiveFilters?: boolean
}

const ctaMessages = defineMessages({
  changeDates: {
    id: 'changeDates',
    defaultMessage: 'Change dates'
  },
  removeFilters: {
    id: 'removeFiltersMessage',
    defaultMessage: 'Remove filters'
  }
})
const getCtaMessage = (hasActiveFilters: Props['hasActiveFilters']) => {
  if (hasActiveFilters) return ctaMessages.removeFilters
  return ctaMessages.changeDates
}

const contentMessages = defineMessages({
  soldOutWithRedirectOffers: {
    id: 'offerBox.soldOutWithRedirectOffers',
    defaultMessage: 'You can still book with these providers:'
  },
  noRoomsAvailable: {
    id: 'noMoreRoomsAvailable',
    defaultMessage: 'There are no rooms available at this property.'
  },
  removeFilters: {
    id: 'offerBox.removeFiltersForMoreResults',
    defaultMessage: 'Remove some filters to see more results.'
  }
})

const getContentMessage = ({
  hasActiveFilters,
  hasOtherOffers
}: Pick<Props, 'hasActiveFilters' | 'hasOtherOffers'>) => {
  if (hasActiveFilters) return contentMessages.removeFilters
  if (hasOtherOffers) return contentMessages.soldOutWithRedirectOffers
  return contentMessages.noRoomsAvailable
}

const handleScrollTop = () => {
  window.scrollTo({top: 0, behavior: 'smooth'})
}
export const AccommodationOfferBoxSoldout = ({
  checkIn,
  checkOut,
  hasOtherOffers = false,
  hasActiveFilters = false
}: Props) => {
  const dispatch = useDispatch()
  const {brandName} = useBrand()

  const hasFiltersAndOtherOffers = hasOtherOffers && hasActiveFilters
  const {setRoomsFilters} = useHotelRoomsFilters()

  const handleRemoveFilters = () => {
    setRoomsFilters([])
  }
  const handleChangeDates = () => {
    handleScrollTop()
    dispatch(showDatePicker(DatePickerType.CheckIn))
  }
  return (
    <ContentWrapper
      paddingX="s500"
      paddingY="s600"
      paddingBottom={hasFiltersAndOtherOffers ? 's500' : 's600'}
      data-id="accommodationOfferBoxSoldout"
    >
      <SoldOutBase
        headerSlot={
          <FormattedMessageWrapper
            id="offerBox.soldOutOnDates"
            defaultMessage="Sold out on {brandName} {checkIn} - {checkOut}"
            values={{
              checkIn: (
                <FormattedDate value={checkIn} month="short" day="2-digit" />
              ),
              checkOut: (
                <FormattedDate value={checkOut} month="short" day="2-digit" />
              ),
              brandName
            }}
          />
        }
        contentSlot={
          <FormattedMessageWrapper
            {...getContentMessage({hasActiveFilters, hasOtherOffers})}
          />
        }
        actionSlot={
          !hasOtherOffers || hasActiveFilters ? (
            <ContentWrapper
              paddingTop={hasFiltersAndOtherOffers ? 's200' : 's500'}
            >
              <Button
                size="xl"
                variant="secondary"
                fullWidth
                onClick={
                  hasActiveFilters ? handleRemoveFilters : handleChangeDates
                }
                dataId="accommodationOfferBoxSoldoutCTA"
              >
                <FormattedMessageWrapper {...getCtaMessage(hasActiveFilters)} />
              </Button>
            </ContentWrapper>
          ) : null
        }
      />
      {hasFiltersAndOtherOffers && (
        <ContentWrapper paddingTop="s300">
          <ContentWrapper paddingTop="s400">
            <Text variant="bodyS">
              <FormattedMessageWrapper
                id="offerBox.bookWithOtherProviders"
                defaultMessage="Or book with another provider:"
              />
            </Text>
          </ContentWrapper>
        </ContentWrapper>
      )}
    </ContentWrapper>
  )
}
